/** Minimal iCalendar (.ics) builder for agenda-evenementen. */

interface IcsEvent {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  time: string; // free text, e.g. "09:30 - 16:00" or "hele dag"
  location?: string | undefined;
  address?: string | undefined;
  description?: string | undefined;
  /**
   * Teams-join-link (ADR 0014: link-doorgifte). Bij intervisie-items levert de
   * API hier de live groepslink aan.
   */
  teamsUrl?: string | undefined;
}

/**
 * Naam en adres samen als één zoekregel, voor zowel de .ics als de kaartlink:
 * agenda-apps en Maps geocoderen die tekst zelf. Naam erbij houden is wat de
 * speld op de zaal zet in plaats van op het kale coördinaat, en een naam
 * alléén is te vaag om op te navigeren ("T hoogt Abcoude" komt uit bij de
 * kerk ernaast) — vandaar dat het adresveld het echte werk doet.
 */
export function fullLocation(place: Place): string | undefined {
  return [place.location, place.address].filter(Boolean).join(', ') || undefined;
}

interface Place {
  location?: string | undefined;
  address?: string | undefined;
}

function escape(value: string): string {
  return value.replace(/([\\;,])/g, '\\$1').replace(/\r?\n/g, '\\n');
}

/**
 * Pull start/end out of the free-text `time` field. Only two HH:MM tokens are
 * meaningful; anything else falls back to an all-day event.
 */
function parseTimes(time: string): { start: string; end: string } | null {
  const hits = [...time.matchAll(/(\d{1,2}):(\d{2})/g)].map(
    (m) => `${m[1]!.padStart(2, '0')}${m[2]}00`,
  );
  if (hits.length === 0) return null;
  if (hits.length === 1) {
    const endHour = Math.min(Number(hits[0]!.slice(0, 2)) + 1, 23);
    return { start: hits[0]!, end: `${String(endHour).padStart(2, '0')}${hits[0]!.slice(2)}` };
  }
  return { start: hits[0]!, end: hits[1]! };
}

/** Next calendar day as YYYYMMDD — all-day DTEND is exclusive per RFC 5545. */
function nextDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y!, m! - 1, d! + 1));
  return next.toISOString().slice(0, 10).replace(/-/g, '');
}

export function buildIcs(event: IcsEvent): string {
  const day = event.date.replace(/-/g, '');
  const times = parseTimes(event.time);
  // ponytail: TZID without an inline VTIMEZONE — Google/Apple/Outlook all resolve
  // Europe/Amsterdam from the IANA name. Inline the component if a client balks.
  const when = times
    ? [
        `DTSTART;TZID=Europe/Amsterdam:${day}T${times.start}`,
        `DTEND;TZID=Europe/Amsterdam:${day}T${times.end}`,
      ]
    : [`DTSTART;VALUE=DATE:${day}`, `DTEND;VALUE=DATE:${nextDay(event.date)}`];

  // De link twee keer: als URL-property (Outlook/Google tonen die als knop) en
  // onderaan de omschrijving, omdat lang niet elke agenda-app de URL-property
  // laat zien — dan is de plakbare link nog steeds in beeld.
  const beschrijving = [
    event.description,
    event.teamsUrl ? `Deelnemen via Teams: ${event.teamsUrl}` : undefined,
  ]
    .filter(Boolean)
    .join('\n\n');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SUPPOHRT//Kennishub//NL',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${event.id}@kennishub.suppohrt.nl`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]|\.\d{3}/g, '')}`,
    ...when,
    `SUMMARY:${escape(event.title)}`,
    ...(fullLocation(event) ? [`LOCATION:${escape(fullLocation(event)!)}`] : []),
    ...(beschrijving ? [`DESCRIPTION:${escape(beschrijving)}`] : []),
    // URL is een URI-waarde: die wordt volgens RFC 5545 niet ge-escaped.
    ...(event.teamsUrl ? [`URL:${event.teamsUrl}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.join('\r\n');
}

export function downloadIcs(event: IcsEvent): void {
  const blob = new Blob([buildIcs(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${event.title.replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 60) || 'evenement'}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Google Maps-zoeklink voor naam + adres samen. */
export function mapsUrl(place: Place): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullLocation(place) ?? '')}`;
}
